/**
 * Drives the game while the page is open: a heartbeat every minute (so the simulation knows the app was open), a refresh
 * of the screen every second, and open/close handling when the tab is hidden or shown. The browser pieces are passed in
 * so this can be tested with fakes.
 */
import type { Game } from "./game";

export interface RuntimeEnv {
  setInterval(fn: () => void, ms: number): number;
  clearInterval(id: number): void;
  isVisible(): boolean;
  /** Returns a function that removes the listener. */
  onVisibilityChange(cb: () => void): () => void;
}

export const HEARTBEAT_MS = 60_000;
export const REFRESH_MS = 1_000;

export function startRuntime(game: Pick<Game, "open" | "tick">, env: RuntimeEnv, onChange: () => void): () => void {
  game.open();
  onChange();

  const heartbeat = env.setInterval(() => {
    if (env.isVisible()) {
      game.tick();
      onChange();
    }
  }, HEARTBEAT_MS);
  const refresh = env.setInterval(onChange, REFRESH_MS);
  const stopListening = env.onVisibilityChange(() => {
    if (env.isVisible()) game.open();
    else game.tick();
    onChange();
  });

  return () => {
    env.clearInterval(heartbeat);
    env.clearInterval(refresh);
    stopListening();
  };
}

export function browserEnv(): RuntimeEnv {
  return {
    setInterval: (fn, ms) => window.setInterval(fn, ms),
    clearInterval: (id) => window.clearInterval(id),
    isVisible: () => document.visibilityState === "visible",
    onVisibilityChange: (cb) => {
      document.addEventListener("visibilitychange", cb);
      return () => document.removeEventListener("visibilitychange", cb);
    },
  };
}
